import { getLocale, isLocale, type Locale } from './locale';

/**
 * World openers: the essay a learner reads *before* a chapter, at
 * `?opener=<slug>`.
 *
 * A level's debrief (web/src/lib/articles.ts) explains what you just did. An
 * opener explains why the next five levels are worth doing at all — what this
 * part of the job is, where it came from, and what it costs not to know it.
 * They are the only writing in the product that is not about a command.
 *
 * Which is why, unlike the debriefs, these are plain static assets and not a
 * Worker route. An article names every command a level used, so a paid
 * level's article is the paid level and it has to go through the entitlement
 * check. An opener contains nothing that solves anything, and the person who
 * most needs to read one has not signed in, has not paid, and is deciding
 * whether this is worth an evening. Putting that behind a gate would be
 * charging admission to the argument for coming in. So: on the CDN, cached at
 * the edge, readable by anyone with the link, and readable on the dev server
 * with no Worker running at all.
 *
 * Two files per language, built by scripts/build-openers.mjs:
 *
 *   /openers.json               the index, a few kilobytes, fetched once by the map
 *   /openers/<slug>.json        one essay, fetched when one is opened
 *   /openers.ar.json            the same index, of the chapters that have Arabic
 *   /openers/<slug>.ar.json     one essay, in Arabic
 *
 * The body file repeats its own title and lede, so a reader arriving on a
 * shared link makes one request rather than two.
 *
 * ## A chapter at a time
 *
 * A language's index lists only what is translated — a chapter with no
 * sidecar is simply absent from it. That is what makes a half-translated
 * course possible, and it is why nothing here treats an untranslated chapter
 * as an error: the index is the translated one merged over the English, and a
 * body that has no translation is fetched in English instead. An Arabic
 * reader who opens a chapter nobody has translated yet gets the essay, in
 * English, with `locale` on it saying so — because the screen has to set
 * `dir` on prose it is about to render (components/opener/Opener.tsx).
 */

/** One world's entry in the index: everything a card on the map needs, and no prose. */
export interface OpenerCard {
  slug: string;
  /**
   * The exact `world:` string from mission.yaml. With {@link OpenerCard.track}
   * this is the join to the missions, and it takes both: a chapter is a
   * (track, world) pair, because two tracks may each have a chapter called
   * "Testing" and they are not the same chapter.
   */
  world: string;
  /**
   * Which track's chapter this introduces. Absent on a card built before
   * openers knew about tracks, and read as `general` when it is — the general
   * track is the one every learner walks and was the only one there was.
   */
  track?: string;
  title: string;
  lede: string;
  minutes: number;
  firstLevel: number;
  lastLevel: number;
  levels: number;
  /**
   * Which language `title` and `lede` are actually in, stamped on the way
   * through rather than carried in the file: the file says it once, at the
   * top, and a card that has been merged in from the English half of a
   * half-translated index has to be able to say it for itself.
   */
  locale?: Locale;
}

/** One world's opener, whole. */
export interface Opener extends OpenerCard {
  body: string;
}

/**
 * What came back. An opener that is not written yet is a state and not a
 * failure — twelve of the fourteen worlds were in exactly that position on
 * the day this shipped — and the screen says so in its own words rather than
 * showing an error.
 */
export type OpenerResult =
  | { state: 'ready'; opener: Opener }
  /** No opener for this world in any language, or no such slug. */
  | { state: 'missing' }
  /** Offline, or a build served without its assets. */
  | { state: 'unavailable' };

/** English is the source language and is the file itself; the rest are suffixed. */
const indexUrl = (locale: Locale) => (locale === 'en' ? '/openers.json' : `/openers.${locale}.json`);
const bodyUrl = (slug: string, locale: Locale) =>
  locale === 'en' ? `/openers/${encodeURIComponent(slug)}.json` : `/openers/${encodeURIComponent(slug)}.${locale}.json`;

/** The shape the build writes. Anything else is treated as no index at all. */
const SCHEMA = 1;

/**
 * The index, fetched at most once per visit **per language**.
 *
 * Keyed by locale rather than held in one variable, for the reason the
 * article cache is: one slot would answer the second language's question with
 * the first language's answer, and it would do it silently, in the direction
 * nobody tests — an English reader sees English and everything looks fine.
 *
 * The promise is cached rather than the result, so the map and the clear
 * overlay asking at the same moment make one request between them. A failure
 * is cached too, and deliberately: the index is a static asset next to the
 * page that just loaded, so a miss means the build did not write one, and
 * retrying on every render would turn a missing file into a request loop.
 */
const indexes = new Map<Locale, Promise<OpenerCard[]>>();

export function fetchOpenerIndex(locale: Locale = getLocale()): Promise<OpenerCard[]> {
  let hit = indexes.get(locale);
  if (!hit) {
    hit = loadIndex(locale);
    indexes.set(locale, hit);
  }
  return hit;
}

/**
 * One language's index is the translated chapters laid over the English ones.
 *
 * Not the translated file on its own: the map draws a card per world and the
 * course has fourteen of them in every language. A chapter nobody has
 * translated yet is a card in English, not a hole in the path.
 */
async function loadIndex(locale: Locale): Promise<OpenerCard[]> {
  if (locale === 'en') return (await readIndex('en')).map((card) => ({ ...card, locale: 'en' as Locale }));

  // Both at once, and the English half through the cache above rather than
  // straight to `readIndex`, so a reader who switches language does not
  // fetch it twice.
  const [english, translated] = await Promise.all([fetchOpenerIndex('en'), readIndex(locale)]);
  const by = new Map(translated.map((card) => [card.slug, { ...card, locale }]));
  const merged: OpenerCard[] = english.map((card) => by.get(card.slug) ?? { ...card, locale: 'en' });

  // A chapter translated but absent from the English index cannot happen —
  // the build writes one from the other — but showing it would beat dropping
  // it if the two files ever came from different deploys.
  const known = new Set(english.map((card) => card.slug));
  for (const card of by.values()) if (!known.has(card.slug)) merged.push(card);
  return merged;
}

async function readIndex(locale: Locale): Promise<OpenerCard[]> {
  try {
    const res = await fetch(indexUrl(locale), { headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const body = (await res.json()) as { schema?: number; locale?: unknown; openers?: OpenerCard[] };
    // A schema this client does not know is not an index. Refusing it is
    // how a stale service worker holding last month's asset fails visibly
    // as "no openers" rather than invisibly as half-rendered cards.
    if (body.schema !== SCHEMA || !Array.isArray(body.openers)) return [];
    // And a language's index that says it is another language is a single
    // page app answering an unknown path with the English file. Treating it
    // as empty falls back a chapter at a time, which is the same answer as
    // "none of this is translated yet".
    if (locale !== 'en' && body.locale !== locale) return [];
    return body.openers;
  } catch {
    return [];
  }
}

/**
 * One opener, by slug and language. Cached per pair for the visit, promise
 * and all, for the same reason the index is — and keyed by the pair for the
 * same reason too: `ar:first-commits` and `en:first-commits` are different
 * files, and a cache that could not tell them apart would hand an Arabic
 * reader the English essay the moment they had read it in English once.
 */
const bodies = new Map<string, Promise<OpenerResult>>();

const bodyKey = (slug: string, locale: Locale) => `${locale}:${slug}`;

export function fetchOpener(slug: string, locale: Locale = getLocale()): Promise<OpenerResult> {
  const key = bodyKey(slug, locale);
  const hit = bodies.get(key);
  if (hit) return hit;

  // Outcomes that could change on a retry are not kept: a reader whose
  // connection comes back gets a real answer next time rather than the cached
  // shrug. `missing` is kept, because a file the build did not write will not
  // appear while the tab is open.
  const run = loadOpener(slug, locale).then((r) => {
    if (r.state === 'unavailable') bodies.delete(key);
    return r;
  });
  bodies.set(key, run);
  return run;
}

/**
 * The asked-for language, then English.
 *
 * `missing` is the only thing that falls back. `unavailable` means nothing
 * answered at all, and asking the same server a second question is not going
 * to go better.
 */
async function loadOpener(slug: string, locale: Locale): Promise<OpenerResult> {
  const asked = await readBody(slug, locale);
  if (asked.state === 'missing' && locale !== 'en') return readBody(slug, 'en');
  return asked;
}

async function readBody(slug: string, locale: Locale): Promise<OpenerResult> {
  let res: Response;
  try {
    res = await fetch(bodyUrl(slug, locale), { headers: { Accept: 'application/json' } });
  } catch {
    // Nothing answered at all: offline, or DNS, or the page is running
    // somewhere its own assets are not.
    return { state: 'unavailable' };
  }

  // 404 is "not written yet", which is a sentence the screen can say.
  if (res.status === 404) return { state: 'missing' };
  // Any other refusal is the assets being unreachable rather than absent.
  if (!res.ok) return { state: 'unavailable' };

  // A 200 that is not JSON means there is no such file.
  //
  // This is not defensive coding, it is the common case: an app served as a
  // single page answers an unknown path with index.html and status 200,
  // which is what both the vite dev server and Static Assets do. Reading
  // only the status here would report a chapter nobody has written yet as
  // "could not load it" — an error, with a Try again button, for a
  // condition that retrying cannot change and that is not an error at all.
  // Twelve of the fourteen worlds were in exactly that state on the day
  // this shipped, so getting the sentence right mattered more than usual.
  try {
    const body = (await res.json()) as Partial<Opener> & { schema?: number; locale?: unknown };
    if (body.schema !== SCHEMA || typeof body.body !== 'string' || typeof body.title !== 'string') {
      return { state: 'missing' };
    }
    // The same check the index makes, and the same reason: a single page app
    // answers `/openers/x.ar.json` with the English file rather than a 404,
    // and an English essay stamped Arabic would be rendered right to left.
    // An absent `locale` fails it too, which is what an English payload has.
    if (locale !== 'en' && (!isLocale(body.locale) || body.locale !== locale)) return { state: 'missing' };
    return { state: 'ready', opener: { ...(body as Opener), locale } };
  } catch {
    return { state: 'missing' };
  }
}

/** The opener for a world, or null for a world that has none yet. */
export function openerForWorld(index: OpenerCard[], world: string, track = 'general'): OpenerCard | null {
  // Both halves of the pair, with `general` standing in on either side for a
  // card or a caller that predates tracks. A name-only match would find the
  // first chapter called "Testing" whichever track asked, and put the wrong
  // essay under a heading that looked right — which is the failure the build
  // used to prevent by refusing to let two chapters share a name at all.
  return index.find((o) => o.world === world && (o.track ?? 'general') === track) ?? null;
}

/**
 * Whether this opener has already been offered on this browser.
 *
 * The same record, for the same reason, as the warm-up's (web/src/lib/
 * warmup.ts): an opener is put in front of a learner once, on the way into a
 * chapter, and after that it is a card on the map they can go back to. A
 * chapter introduction that reappeared every time would become a thing to
 * dismiss rather than a thing to read.
 *
 * Per browser and not part of progress, again like the warm-up: it says
 * nothing about what the learner knows, only that the offer has been made,
 * and losing it costs one extra click.
 *
 * Not keyed by language, unlike everything above it. The record is about the
 * reader and not about the file: somebody who read a chapter's introduction
 * in English has read it, and being shown it again for having switched
 * language would be the product forgetting them rather than translating.
 */
const key = (slug: string) => `engineeros.opener.${slug}`;

/** False whenever storage is unavailable, which makes the offer again rather than swallow it. */
export function openerSeen(slug: string): boolean {
  try {
    return localStorage.getItem(key(slug)) === '1';
  } catch {
    return false;
  }
}

export function markOpenerSeen(slug: string): void {
  try {
    localStorage.setItem(key(slug), '1');
  } catch {
    /* private mode, or storage disabled */
  }
}
